import type React from 'react';
import type { VideoJsPlayer } from 'video.js';

import type { PlayerApi } from '../player.config';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useLogger } from '../../../contexts/logger-context';
import { clamp } from '../../../utils/clamp';
import { floor } from '../../../utils/math';
import { msToS, sToMs } from '../../../utils/time';
import { createPlayerApi } from '../create-player-api';
import { VideoJsNativeEvent } from '../videojs-event';

type Props = {
  player: VideoJsPlayer;
  setTotalTime: React.Dispatch<React.SetStateAction<number>>;
  setStartTime: React.Dispatch<React.SetStateAction<number>>;
  setEndTime: React.Dispatch<React.SetStateAction<number>>;
  setVolume: React.Dispatch<React.SetStateAction<number>>;
  setIsMuted: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEnded: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  setBufferPercentage: React.Dispatch<React.SetStateAction<number>>;
  setMediaError: React.Dispatch<React.SetStateAction<MediaError | null>>;
  setSelectedSubtitleId: React.Dispatch<React.SetStateAction<string>>;
  setVolumeChangeSupported: React.Dispatch<React.SetStateAction<boolean>>;
  setMuteChangeSupported: React.Dispatch<React.SetStateAction<boolean>>;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onReady?: (api: PlayerApi) => void;
  videoLayout?: string;
  defaultVolume: number;
  startTimeRef: React.RefObject<number>;
  endTimeRef: React.RefObject<number>;
  playFnc: (player?: VideoJsPlayer) => Promise<void>;
  autoplayFnc: (player?: VideoJsPlayer) => Promise<void>;
  autoplayMutedFnc: (player?: VideoJsPlayer) => Promise<void>;
};

export function usePlayerReady({
  player,
  setTotalTime,
  setStartTime,
  setEndTime,
  setVolume,
  setIsMuted,
  setCurrentTime,
  setIsPlaying,
  setIsEnded,
  setIsFullscreen,
  setBufferPercentage,
  setMediaError,
  setSelectedSubtitleId,
  setVolumeChangeSupported,
  setMuteChangeSupported,
  onFullscreenChange: handleFullscreenChange,
  onReady,
  videoLayout,
  defaultVolume,
  startTimeRef,
  endTimeRef,
  playFnc,
  autoplayFnc,
  autoplayMutedFnc,
}: Props): {
  requestCurrentTimeChange: (time: number) => void;
  requestCurrentTimeDeltaChange: (deltaMs: number) => void;
} {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'Player' }), [mainLogger]);
  // Separate logger for source-related logs
  const loggerSource = useMemo(() => mainLogger.getSubLogger({ name: 'PlayerSource' }), [mainLogger]);

  const startFromTimeRef = useRef(0);
  const canSeekRef = useRef(false);

  const requestCurrentTimeChange = useCallback(
    (time: number) => {
      logger.debug('Requesting current time change to:', time, canSeekRef.current);

      const adjustedDuration = sToMs(floor(player.duration(), 0.1)); // It seems we can't seek to the exact duration, so we need to floor it to the nearest 0.1 seconds
      const adjustedTime = clamp(time, 0, adjustedDuration);

      setCurrentTime(adjustedTime);

      // On some devices (like iOS), the player may not be able to seek until the video has started playing, so we defer calling player.currentTime(...) until it is allowed.
      if (canSeekRef.current) {
        player.currentTime(msToS(adjustedTime));
      } else {
        startFromTimeRef.current = time;
      }
    },
    [logger, player, setCurrentTime]
  );
  const requestCurrentTimeDeltaChange = useCallback(
    (deltaMs: number) => {
      const currentTime = canSeekRef.current ? sToMs(player.currentTime()) : startFromTimeRef.current;

      requestCurrentTimeChange(currentTime + deltaMs);
    },
    [player, requestCurrentTimeChange]
  );

  useEffect(() => {
    // hide volume controls when they're not supported by the current tech
    const getFeaturesSupport = () => {
      const { featuresVolumeControl, featuresMuteControl } = player.tech(true);

      setVolumeChangeSupported(featuresVolumeControl ?? true);
      setMuteChangeSupported(featuresMuteControl ?? true);
    };

    getFeaturesSupport();

    const updateDuration = () => {
      const duration = player.duration();
      const totalTime = sToMs(duration);

      setTotalTime(totalTime);
      setEndTime(totalTime);
    };

    const onVolumeChange = () => {
      setVolume(player.volume());
      setIsMuted(player.muted());
    };

    const onTimeUpdate = () => {
      setCurrentTime(sToMs(player.currentTime()));
    };

    const onFullscreenChange = () => {
      const isFullscreen = player.isFullscreen();
      player.fluid(!isFullscreen && (!videoLayout || videoLayout === 'fluid'));
      setIsFullscreen(isFullscreen);
      handleFullscreenChange?.(isFullscreen);
    };

    const onPlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
    };

    const onLoadStart = () => {
      setIsPlaying(!player.paused());
      updateDuration();
      getFeaturesSupport();
    };

    const onPause = () => setIsPlaying(false);
    const onDurationChange = () => updateDuration();
    const onProgress = () => setBufferPercentage(player.bufferedPercent());
    const onEnded = () => setIsEnded(true);
    const onError = () => {
      logger.error('Player - onError - setMediaError:', player.error());
      setMediaError(player.error());
    };
    const onReset = () => {
      logger.debug('Player - onReset');
      setMediaError(null); // Reset the error state when the player is reset e.g. when changing the source
    };
    const onLoadedData = () => {
      // After the player has loaded the data, we are now sure we can seek to the start time (if it is set)
      canSeekRef.current = true;
      if (startFromTimeRef.current > 0) {
        const adjustedDuration = sToMs(floor(player.duration(), 0.1)); // It seems we can't seek to the exact duration, so we need to floor it to the nearest 0.1 seconds
        const adjustedTime = clamp(startFromTimeRef.current, 0, adjustedDuration);
        player.currentTime(msToS(adjustedTime));
        startFromTimeRef.current = 0;
      }
    };
    const onSourceSet = () => {
      loggerSource.info(`Player event: sourceSet, source:`, player.currentSources());
      setMediaError(null); // Reset the error state when the source is set
    };

    // Log all player events
    const logPlayerEvent = (event: Event) => {
      logger.debug('Player event:', event.type);
    };
    const eventLoggers = Object.values(VideoJsNativeEvent).map((event) => {
      player.on(event, logPlayerEvent);
      return { event, logPlayerEvent };
    });

    player.on(VideoJsNativeEvent.PLAY, onPlay);
    player.on(VideoJsNativeEvent.PAUSE, onPause);
    player.on(VideoJsNativeEvent.VOLUME_CHANGE, onVolumeChange);
    player.on(VideoJsNativeEvent.TIME_UPDATE, onTimeUpdate);
    player.on(VideoJsNativeEvent.FULLSCREEN_CHANGE, onFullscreenChange);
    player.on(VideoJsNativeEvent.PROGRESS, onProgress);
    player.on(VideoJsNativeEvent.ENDED, onEnded);
    player.on(VideoJsNativeEvent.ERROR, onError);
    player.on(VideoJsNativeEvent.PLAYER_RESET, onReset);
    player.on(VideoJsNativeEvent.SOURCE_SET, onSourceSet);

    // We do not want to/need to throttle duration changes,
    // as they should always display the changed duration as
    // it has changed.
    player.on(VideoJsNativeEvent.DURATION_CHANGE, onDurationChange);

    // Listen to loadstart because the player duration is reset when a new media element is loaded,
    // but the durationchange on the user agent will not fire.
    // @see [Spec]{@link https://www.w3.org/TR/2011/WD-html5-20110113/video.html#media-element-load-algorithm}
    player.on(VideoJsNativeEvent.LOAD_START, onLoadStart);

    player.on(VideoJsNativeEvent.LOADED_DATA, onLoadedData);

    if (onReady) {
      const props = {
        handleSubtitleSelection: setSelectedSubtitleId,
        setStartTime,
        setEndTime,
        getTrimStartTime: () => startTimeRef.current ?? 0,
        getTrimEndTime: () => endTimeRef.current ?? 0,
        options: { defaultVolume: defaultVolume },
        requestCurrentTimeChange,
        playFnc,
        autoplayFnc,
        autoplayMutedFnc,
      };
      const playerApi = createPlayerApi(player, props);
      onReady(playerApi);
    }

    logger.info('Player ready');

    return () => {
      player.off(VideoJsNativeEvent.PLAY, onPlay);
      player.off(VideoJsNativeEvent.PAUSE, onPause);
      player.off(VideoJsNativeEvent.VOLUME_CHANGE, onVolumeChange);
      player.off(VideoJsNativeEvent.TIME_UPDATE, onTimeUpdate);
      player.off(VideoJsNativeEvent.FULLSCREEN_CHANGE, onFullscreenChange);
      player.off(VideoJsNativeEvent.PROGRESS, onProgress);
      player.off(VideoJsNativeEvent.ENDED, onEnded);
      player.off(VideoJsNativeEvent.ERROR, onError);
      player.off(VideoJsNativeEvent.DURATION_CHANGE, onDurationChange);
      player.off(VideoJsNativeEvent.LOAD_START, onLoadStart);
      player.off(VideoJsNativeEvent.PLAYER_RESET, onReset);
      player.off(VideoJsNativeEvent.LOADED_DATA, onLoadedData);
      player.off(VideoJsNativeEvent.SOURCE_SET, onSourceSet);

      eventLoggers.forEach(({ event, logPlayerEvent }) => {
        player.off(event, logPlayerEvent);
      });
    };
  }, [
    player,
    setStartTime,
    setEndTime,
    logger,
    loggerSource,
    onReady,
    setTotalTime,
    setVolume,
    setIsMuted,
    setCurrentTime,
    videoLayout,
    setIsFullscreen,
    handleFullscreenChange,
    setIsPlaying,
    setIsEnded,
    setMediaError,
    setBufferPercentage,
    setSelectedSubtitleId,
    setVolumeChangeSupported,
    setMuteChangeSupported,
    defaultVolume,
    startTimeRef,
    endTimeRef,
    requestCurrentTimeChange,
    playFnc,
    autoplayFnc,
    autoplayMutedFnc,
  ]);

  return {
    requestCurrentTimeChange,
    requestCurrentTimeDeltaChange,
  };
}
